import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { Avatar, horaAmPm, minutosTexto, useAbrirFicha } from '../ui';

const TAMANO = 30;          // diámetro de cada cara
const SEPARACION = 3;
const FILAS_MAXIMAS = 9;
// La regla llega hasta 2,5 h después de la entrada; quien llegue más tarde queda en el borde
const MAXIMO_DESPUES = 150;
const ALTO_EJE = 34;

const aMinutos = (hhmm) => {
    const [h, m] = hhmm.split(':').map(Number);
    return h * 60 + m;
};
const etiquetaHora = (min) => {
    const h = Math.floor(min / 60);
    return `${h % 12 || 12} ${h < 12 ? 'a. m.' : 'p. m.'}`;
};

/**
 * Las llegadas del día: cada cara es una persona, puesta en la hora en que marcó
 * entrada. La línea azul es la hora de entrada del horario; a la derecha, tarde.
 * Las caras se apilan cuando llegan casi al mismo tiempo.
 */
const LlegadasFranja = ({ personas, horario, ahora, esHoy }) => {
    const abrirFicha = useAbrirFicha();
    const reducir = useReducedMotion();
    const contenedor = useRef(null);
    const [ancho, setAncho] = useState(0);
    const [encima, setEncima] = useState(null);
    const vistos = useRef(null);   // ids ya dibujados: solo los nuevos se animan después de la primera carga

    useLayoutEffect(() => {
        const el = contenedor.current;
        if (!el) return undefined;
        const medir = () => setAncho(el.clientWidth);
        medir();
        const obs = new ResizeObserver(medir);
        obs.observe(el);
        return () => obs.disconnect();
    }, []);

    const llegadas = useMemo(
        () => personas.filter((p) => p.entrada).sort((a, b) => a.entrada.localeCompare(b.entrada)),
        [personas],
    );

    const entrada = horario ? aMinutos(horario.entrada) : 8 * 60;
    const tolerancia = horario?.tolerancia_min || 0;
    const minAhora = ahora ? aMinutos(ahora.slice(11, 16)) : null;

    // Rango del eje: de una hora antes de la entrada (o la primera llegada) a una después (o la última)
    const tiempos = llegadas.map((p) => aMinutos(p.entrada));
    const inicio = Math.floor(Math.min(entrada - 45, ...tiempos.map((t) => t - 5)) / 60) * 60;
    let fin = Math.ceil((Math.min(Math.max(entrada + 60, ...tiempos), entrada + MAXIMO_DESPUES) + 10) / 60) * 60;
    if (esHoy && minAhora && minAhora > fin - 10 && minAhora < fin + 120) fin = Math.ceil((minAhora + 10) / 60) * 60;
    const x = (min) => ((min - inicio) / (fin - inicio)) * Math.max(ancho - TAMANO, 1) + TAMANO / 2;

    // Apilado: cada cara va en la fila más baja donde no pisa a otra
    const { colocadas, sobrantes, filas } = useMemo(() => {
        const ultimas = [];
        const lista = [];
        const resto = {};
        llegadas.forEach((p) => {
            const px = x(Math.min(aMinutos(p.entrada), fin));
            let fila = ultimas.findIndex((u) => px - u >= TAMANO + SEPARACION);
            if (fila === -1) fila = ultimas.length;
            if (fila >= FILAS_MAXIMAS) {
                const clave = Math.round(px / (TAMANO + SEPARACION));
                resto[clave] = resto[clave] || { x: px, personas: [] };
                resto[clave].personas.push(p);
                return;
            }
            ultimas[fila] = px;
            lista.push({ ...p, px, fila });
        });
        return { colocadas: lista, sobrantes: Object.values(resto), filas: Math.max(ultimas.length, 2) };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [llegadas, ancho, inicio, fin]);

    const alto = Math.min(filas, FILAS_MAXIMAS) * (TAMANO + SEPARACION) + 26;
    const primeraVez = vistos.current === null;
    useEffect(() => {
        // Recién cuando ya se dibujó con ancho: así la primera vez sí se anima la entrada en orden
        if (ancho > 0) vistos.current = new Set(llegadas.map((p) => p.empleado_id));
    }, [llegadas, ancho]);

    const hayDespues = tiempos.some((t) => t > fin);
    const marcas = [];
    for (let m = inicio; m <= fin; m += 15) marcas.push(m);

    return (
        <div ref={contenedor} className="relative select-none" style={{ height: alto + ALTO_EJE }}>
            {ancho > 0 && (
                <>
                    {/* Tarde: del límite de entrada en adelante */}
                    <div
                        className="absolute top-0 rounded-r-xl bg-amber-50/80"
                        style={{ left: x(entrada + tolerancia), right: 0, bottom: ALTO_EJE }}
                        aria-hidden
                    />
                    {tolerancia > 0 && (
                        <div className="absolute top-0 bg-sky-50" style={{ left: x(entrada), width: x(entrada + tolerancia) - x(entrada), bottom: ALTO_EJE }} aria-hidden />
                    )}

                    {/* Línea de entrada */}
                    <div className="absolute top-0 w-[2px] bg-sura-blue rounded-full" style={{ left: x(entrada) - 1, bottom: ALTO_EJE - 6 }} aria-hidden />
                    <span
                        className="absolute -top-0.5 text-[11px] font-semibold text-sura-blue bg-white/90 px-1.5 rounded whitespace-nowrap"
                        style={{ left: x(entrada) + 6 }}
                    >
                        Entrada {horaAmPm(horario?.entrada || '08:00')}
                    </span>

                    {/* Ahora */}
                    {esHoy && minAhora >= inicio && minAhora <= fin && (
                        <>
                            <div className="absolute top-0 w-px bg-cyan-400" style={{ left: x(minAhora), bottom: ALTO_EJE - 6 }} aria-hidden />
                            <span className="absolute -top-0.5 text-[11px] font-medium text-cyan-700 whitespace-nowrap -translate-x-full pr-1.5" style={{ left: x(minAhora) }}>
                                Ahora
                            </span>
                        </>
                    )}

                    {/* Eje de horas */}
                    <div className="absolute left-0 right-0 border-t border-slate-200" style={{ bottom: ALTO_EJE - 6 }} aria-hidden />
                    {marcas.map((m) => (
                        <div key={m} className="absolute" style={{ left: x(m), bottom: 0, height: ALTO_EJE }} aria-hidden>
                            <div className={`absolute left-0 w-px ${m % 60 === 0 ? 'h-2.5 bg-slate-300' : 'h-1.5 bg-slate-200'}`} style={{ top: 0 }} />
                            {m % 60 === 0 && (
                                // Quienes llegaron después del final de la regla quedan en el borde: se avisa en la última hora
                                <span className={`absolute top-3 text-[11px] text-slate-400 whitespace-nowrap tabular-nums ${
                                    m === fin && hayDespues ? '-translate-x-full' : '-translate-x-1/2'}`}>
                                    {etiquetaHora(m)}{m === fin && hayDespues ? ' o después' : ''}
                                </span>
                            )}
                        </div>
                    ))}

                    {/* Caras */}
                    {colocadas.map((p, i) => {
                        const tarde = p.retardo_min > 0;
                        const nuevo = !primeraVez && vistos.current && !vistos.current.has(p.empleado_id);
                        return (
                            <motion.button
                                key={p.empleado_id}
                                type="button"
                                initial={reducir ? false : (primeraVez ? { opacity: 0, y: 10 } : nuevo ? { opacity: 0, scale: 0.4 } : false)}
                                animate={{ opacity: 1, y: 0, scale: 1 }}
                                transition={{ delay: primeraVez ? Math.min(i * 0.012, 1.2) : 0, type: 'spring', stiffness: 380, damping: 26 }}
                                onClick={() => abrirFicha(p.empleado_id)}
                                onMouseEnter={() => setEncima(p)} onMouseLeave={() => setEncima(null)}
                                onFocus={() => setEncima(p)} onBlur={() => setEncima(null)}
                                aria-label={`${p.nombre}, entró a las ${horaAmPm(p.entrada)}${tarde ? `, ${minutosTexto(p.retardo_min)} tarde` : ''}`}
                                className={`absolute rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400 ${
                                    tarde ? 'ring-2 ring-amber-400' : 'ring-2 ring-white'
                                } hover:z-10 hover:scale-110 transition-transform`}
                                style={{
                                    left: p.px - TAMANO / 2,
                                    bottom: ALTO_EJE + 2 + p.fila * (TAMANO + SEPARACION),
                                }}
                            >
                                <Avatar persona={p} size={TAMANO} />
                            </motion.button>
                        );
                    })}

                    {/* Más de las que caben en una columna */}
                    {sobrantes.map((g) => (
                        <span
                            key={g.x}
                            className="absolute rounded-full bg-slate-800 text-white text-[11px] font-semibold flex items-center justify-center"
                            style={{ left: g.x - TAMANO / 2, bottom: ALTO_EJE + 2 + FILAS_MAXIMAS * (TAMANO + SEPARACION), width: TAMANO, height: TAMANO }}
                            title={g.personas.map((p) => `${p.nombre} ${horaAmPm(p.entrada)}`).join('\n')}
                        >
                            +{g.personas.length}
                        </span>
                    ))}

                    {/* Detalle de la cara bajo el puntero */}
                    {encima && (
                        <div
                            className="absolute z-20 pointer-events-none -translate-x-1/2 px-3 py-2 rounded-xl bg-slate-900 text-white shadow-lg whitespace-nowrap"
                            style={{
                                left: Math.min(Math.max(x(Math.min(aMinutos(encima.entrada), fin)), 90), ancho - 90),
                                bottom: ALTO_EJE + 10 + ((colocadas.find((c) => c.empleado_id === encima.empleado_id)?.fila ?? 0) + 1) * (TAMANO + SEPARACION),
                            }}
                        >
                            <p className="text-sm font-medium">{encima.nombre}</p>
                            <p className="text-xs text-slate-300">
                                Entró a las {horaAmPm(encima.entrada)}
                                {encima.retardo_min > 0 && <span className="text-amber-300">, {minutosTexto(encima.retardo_min)} tarde</span>}
                            </p>
                            {encima.departamento && <p className="text-xs text-slate-400">{encima.departamento}</p>}
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default LlegadasFranja;
